import type {
	ConstantsNotificationChannelResponse,
	ConstantsNotificationRuleResponse,
} from "@gosaas/types";
import { useQuery } from "@tanstack/react-query";
import { apiClient, requestWithHandling } from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

export const useFetchNotificationChannels = (
	teamID: string,
	enabled: boolean = true,
) => {
	const {
		data: channels,
		isLoading,
		refetch: refetchChannels,
	} = useQuery({
		queryKey: ["notification-channels", teamID],
		enabled: !!teamID && enabled,
		queryFn: async () => {
			try {
				const response = await requestWithHandling<
					ConstantsNotificationChannelResponse[]
				>(() => apiClient.api.teamNotificationsChannelsList(teamID));
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to load channels");
			} catch (err) {
				reportQueryError(err, {
					title: "Failed to load notification channels",
				});
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { channels, isLoading, refetchChannels };
};

export const useFetchNotificationRules = (teamID: string) => {
	const {
		data: rules,
		isLoading,
		refetch: refetchRules,
	} = useQuery({
		queryKey: ["notification-rules", teamID],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<
					ConstantsNotificationRuleResponse[]
				>(() => apiClient.api.teamNotificationsRulesList(teamID));
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to load rules");
			} catch (err) {
				reportQueryError(err, {
					title: "Failed to load notification rules",
				});
			}
		},
		enabled: !!teamID,
		retry: 1,
		retryDelay: 1000,
	});

	return { rules, isLoading, refetchRules };
};

export const useFetchNotificationEvents = (
	teamID: string,
	enabled: boolean = true,
) => {
	const { data: events, isLoading: isLoadingEvents } = useQuery({
		queryKey: ["notification-events", teamID],
		enabled: !!teamID && enabled,
		queryFn: async () => {
			try {
				const response = await requestWithHandling<string[]>(() =>
					apiClient.api.teamNotificationsEventsList(teamID),
				);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to load events");
			} catch (err) {
				reportQueryError(err, {
					title: "Failed to load notification events",
				});
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { events, isLoadingEvents };
};
